/**
 * AzuraCast — prvi člen kaskade (T3 §3).
 *
 * AzuraCast je odprtokodna programska oprema, s katero si postaja postavi
 * svoj radio; spodaj poganja Icecast ali Shoutcast, navzven pa ponuja svoj
 * API in **SSE**. Med skupnostnimi in neodvisnimi radii je zelo pogosta.
 *
 * Zakaj je prvi člen: SSE pomeni, da postaja menjavo skladbe **pove sama** v
 * trenutku, ko se zgodi. Namesto zahtevka vsakih 15–30 s teče ena povezava,
 * zamik pa pade z »do pol minute« na nič.
 *
 * ⚠️ **Vse tu je izmerjeno na živih postajah 2026-07-20**, ne prebrano iz
 * dokumentacije: `access-control-allow-origin: *` na `/api/nowplaying/…`,
 * odsev izvora na SSE, in oblika sporočil spodaj. T3 §6 je do takrat CORS
 * AzuraCasta vodil kot **nepreverjen**.
 */

import { parseStreamTitle, type Track } from './icy-parse';

/**
 * Kratka oznaka postaje iz naslova toka.
 *
 * AzuraCast servira tok na `/listen/{shortcode}/…`. Nekatere postavitve
 * uporabljajo namesto tega gola vrata (`host:8010/radio.mp3`) in tam oznake
 * iz naslova **ni**; takrat AzuraCast preskočimo. Alternativa bi bila klic
 * `/api/nowplaying` brez oznake, ki pa vrne **vse** postaje gostitelja —
 * izmerjeno do 996 KB na en zahtevek, kar je za zaznavanje nesorazmerno.
 */
export function azuracastShortcode(streamUrl: string): string | null {
  try {
    const { pathname } = new URL(streamUrl);
    const match = /^\/listen\/([^/]+)\//.exec(pathname);
    const shortcode = match?.[1];
    return shortcode === undefined || shortcode === '' ? null : decodeURIComponent(shortcode);
  } catch {
    return null;
  }
}

export function azuracastNowPlayingUrl(streamUrl: string, shortcode: string): string | null {
  try {
    return new URL(`/api/nowplaying/${encodeURIComponent(shortcode)}`, streamUrl).toString();
  } catch {
    return null;
  }
}

/**
 * Naslov SSE.
 *
 * `cf_connect` je JSON s seznamom naročnin — kanal je `station:{shortcode}`.
 * `recover: true` pove strežniku, naj ob ponovni povezavi pošlje zadnje
 * stanje, sicer bi vrstica po vsakem prekinjenem omrežju ostala prazna do
 * naslednje menjave skladbe.
 */
export function azuracastSseUrl(streamUrl: string, shortcode: string): string | null {
  try {
    const url = new URL('/api/live/nowplaying/sse', streamUrl);
    url.searchParams.set(
      'cf_connect',
      JSON.stringify({ subs: { [`station:${shortcode}`]: { recover: true } } }),
    );
    return url.toString();
  } catch {
    return null;
  }
}

interface AzuraSong {
  text?: string;
  artist?: string;
  title?: string;
  art?: string;
}

interface AzuraNowPlaying {
  now_playing?: { song?: AzuraSong };
  is_online?: boolean;
}

export interface AzuraResult {
  track: Track | null;
  artworkUrl?: string;
}

/**
 * Skladba in ovitek iz AzuraCastovega odgovora.
 *
 * **Ločenima poljema `artist` in `title` se verjame prej kot `text`.**
 * Izmerjeno na Radiu METEOR: `text` je »Chris Isaak - Chris Isaak - Blue
 * Hotel«, ločeni polji pa čisti »Chris Isaak« in »Blue Hotel«. Sestavljeni
 * niz gre skozi razčlenjevalnik le kot zasilna pot.
 */
export function parseAzuracast(json: unknown): AzuraResult | null {
  const body = json as AzuraNowPlaying | null;
  const song = body?.now_playing?.song;
  if (song === undefined) return null;

  const artist = song.artist?.trim() ?? '';
  const title = song.title?.trim() ?? '';

  let track: Track | null;
  if (title !== '') {
    const raw = artist === '' ? title : `${artist} - ${title}`;
    track = parseStreamTitle(raw);
  } else {
    track = parseStreamTitle(song.text ?? '');
  }

  const art = song.art?.trim() ?? '';
  return art === '' ? { track } : { track, artworkUrl: art };
}

/**
 * `np` iz SSE sporočila (protokol Centrifugo).
 *
 * Dve obliki, obe potrebni:
 *   1. **prvo** sporočilo je `connect` z `subs[kanal].publications[].data.np`
 *      — brez tega vrstica do prve menjave skladbe ostane prazna
 *   2. **nadaljnja** so `pub.data.np`
 *
 * Vmes strežnik pošilja prazne objekte kot utrip; ti dajo `null`.
 */
export function parseSseMessage(json: unknown): unknown | null {
  const body = json as {
    connect?: { subs?: Record<string, { publications?: { data?: { np?: unknown } }[] }> };
    pub?: { data?: { np?: unknown } };
    push?: { pub?: { data?: { np?: unknown } } };
  } | null;

  const direct = body?.pub?.data?.np ?? body?.push?.pub?.data?.np;
  if (direct !== undefined) return direct;

  for (const sub of Object.values(body?.connect?.subs ?? {})) {
    const np = sub.publications?.[0]?.data?.np;
    if (np !== undefined) return np;
  }
  return null;
}
